import themeV2 from '../dap';
import { mgPrisBlue } from './colors';
import { PaletteOptions, Theme, createTheme } from '@mui/material';
import { Overlays } from '@mui/material/styles';
import { ChartPalette, chartDark, chartLight } from './chartPalette';
import { componentsStyles } from './compontentsStyles';
import { SemanticPalette, semanticDark, semanticLight } from './semanticPalette';
import { typography } from './typography';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
    '3xl': true;
  }
}

// Spread keeps `semantic`/`chart` out of the PaletteOptions excess-property check in apps that do
// not load the mgPris theme augmentation (globalTypes/mgpris).
const semantic = (palette: SemanticPalette) => ({ semantic: palette });
const chart = (palette: ChartPalette) => ({ chart: palette });

const lightPalette: PaletteOptions = {
  ...themeV2.palette,
  ...semantic(semanticLight),
  ...chart(chartLight),
  primary: {
    ...mgPrisBlue,
    main: mgPrisBlue[800],
    dark: mgPrisBlue[900],
    light: mgPrisBlue[500],
    contrastText: themeV2.palette.common.white,
  },
  secondary: {
    ...themeV2.palette.secondary,
    light: themeV2.palette.secondary[500],
    main: themeV2.palette.secondary[900],
    dark: themeV2.palette.secondary.A100,
    contrastText: themeV2.palette.common.white,
  },

  grey: {
    ...themeV2.palette.grey,
    50: '#FAFAFA',
    300: '#F9FAFB',
  },
  text: {
    primary: themeV2.palette.grey[800],
    secondary: themeV2.palette.grey.A400,
    disabled: themeV2.palette.grey.A200,
  },
  error: {
    light: themeV2.palette.warning[900],
    main: themeV2.palette.error.main,
    dark: themeV2.palette.error.dark,
    contrastText: themeV2.palette.text.primary,
  },
  gradient: {
    ...themeV2.palette.gradient,
    primary: `linear-gradient(292deg, ${mgPrisBlue[800]} 15.01%, ${mgPrisBlue[700]} 32.03%, ${mgPrisBlue[600]} 50.5%, ${mgPrisBlue[500]} 98.63%)`,
    tertiary: `linear-gradient(292deg, #6E1FBC 15.01%, #8A13B4 32.03%, #991AC6 50.5%, #CA2CCD 98.63%)`,
  },
  background: {
    ...themeV2.palette.background,
    default: mgPrisBlue.A200,
    secondary: mgPrisBlue.A100,
  },
  divider: 'rgba(191, 191, 200, 0.4)',
  nav: {
    text: '#3C3F4C',
    textMuted: '#696C70',
    surface: '#F3F3F7',
    activeLight: '#EBF0F6',
    activeMid: '#E6E9F2',
    activeDark: '#DEE4EE',
  },
};

const midnightPrimary = '#5F64EC';
const midnightTextSecondary = '#CDCFDF';

// "Midnight": the light grey ramp inverted on a blue-tinted dark base.
const midnightGrey = {
  50: '#1B1C3C',
  100: '#242547',
  200: '#4B4C6B',
  300: '#1E2142',
  400: '#83849A',
  500: '#9898AC',
  600: '#AEAFC2',
  700: '#C3C4D4',
  800: '#D8D9E6',
  900: '#ECEDF7',
  A100: '#212242',
  A200: 'rgba(180, 181, 198, 0.65)',
  A400: midnightTextSecondary,
  A700: '',
  main: midnightTextSecondary,
  contrastText: themeV2.palette.common.black,
};

const darkPalette: PaletteOptions = {
  ...semantic(semanticDark),
  ...chart(chartDark),
  primary: {
    ...mgPrisBlue,
    800: midnightPrimary,
    900: '#4D52E8',
    A100: '#1E2142',
    A200: '#0E0F20',
    main: midnightPrimary,
    light: '#9598F6',
    dark: '#4D52E8',
    contrastText: themeV2.palette.common.white,
  },
  secondary: {
    ...themeV2.palette.secondary,
    light: '#FFCE6F',
    main: '#FFB527',
    dark: '#E37500',
    contrastText: themeV2.palette.common.white,
  },
  warning: {
    ...themeV2.palette.warning,
    main: '#FF7A66',
  },
  success: {
    ...themeV2.palette.success,
    main: '#3DBF4A',
  },
  info: {
    ...themeV2.palette.info,
    main: '#8ED3F7',
  },
  error: {
    main: '#FF7B72',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: themeV2.palette.common.white,
  },
  grey: midnightGrey,
  text: {
    primary: '#F6F7FF',
    secondary: midnightTextSecondary,
    disabled: midnightGrey.A200,
  },
  common: themeV2.palette.common,
  gradient: {
    ...themeV2.palette.gradient,
    primary: `linear-gradient(292deg, ${midnightPrimary} 15.01%, ${midnightPrimary}CC 32.03%, ${midnightPrimary}B2 50.5%, ${midnightPrimary}99 98.63%)`,
    secondary: 'linear-gradient(316deg, #FFAD0F 13%, #FFBD3F 87%)',
    tertiary: `linear-gradient(292deg, #6E1FBC 15.01%, #8A13B4 32.03%, #991AC6 50.5%, #CA2CCD 98.63%)`,
  },
  background: {
    default: '#040409',
    paper: '#15162A',
    secondary: '#1E2142',
  },
  divider: 'rgba(148, 148, 164, 0.22)',
  action: {
    active: midnightTextSecondary,
  },
  nav: {
    text: '#ECEDF7',
    textMuted: midnightTextSecondary,
    surface: '#212242',
    activeLight: '#1E2142',
    activeMid: '#242547',
    activeDark: '#2D2F5A',
  },
};

// Midnight expresses elevation through its own surface tones, so MUI's white elevation overlay
// on dark Paper only desaturates them and makes nested paper-coloured content stand out.
const noOverlays = Array(25).fill('none') as Overlays;

const mgPrisTheme: Theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data' },
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette, overlays: noOverlays },
  },
  typography: typography,
  shape: themeV2.shape,
  spacing: 8,
  shadows: themeV2.shadows,
  components: componentsStyles,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 2000,
      '3xl': 2500,
    },
  },
});

export default mgPrisTheme;
